import { last } from 'lodash'
import { useQuery, useQueryClient, type UseQueryOptions } from 'react-query'

import {
  integrationsClient,
  integrationsQueryKey,
} from '@webapp/api/integrations/client'
import type { Paths } from '@webapp/api/integrations/client'
import { integrationsKeys } from '@webapp/api/integrations/hooks/queryKey.factory'
import { getCurrentSubdomain } from '@webapp/api/integrations/hooks/utils'
import { IntegrationSyncResponseStatuses } from '@webapp/models/enums/IntegrationSyncResponseStatus'

type TData = Paths.ListSync.Responses.$200
type TError = Paths.ListSync.Responses.$422

type PathParameters = Paths.ListSync.PathParameters
type QueryParameters = Paths.ListSync.QueryParameters

export type GetListSyncQueryConfig = Omit<
  UseQueryOptions<TData, TError>,
  'queryKey' | 'queryFn'
>

const queryKey = 'listSync'

export const useGetListSyncQueryKey = queryKey

export const listSync = async (
  params: Partial<PathParameters & QueryParameters>
) => {
  const subdomain = getCurrentSubdomain()

  if (
    !params.local_entity_ref ||
    !params.integration_id ||
    !params.local_entity
  )
    return

  try {
    const response = await integrationsClient.listSync({
      integration_id: params.integration_id,
      local_entity: params.local_entity,
      local_entity_ref: params.local_entity_ref,
      'X-Procurify-Tenant': subdomain,
    })
    return response.data
  } catch (e: any) {
    // Throw non-404 errors so they can be caught by `onClickRefreshButton`
    if (e?.response?.status !== 404) {
      throw e
    }
  }
}

export type GetListSyncQueryVariables = {
  localEntity: string
  localEntityRef: number | string
  integrationId: string
}

export const isGetListSyncKey = (
  key: any
): key is ReturnType<(typeof integrationsKeys)['listSync']> => {
  const [integrationsKeyId, queryKeyId] = key

  return (
    integrationsKeyId === integrationsQueryKey &&
    queryKeyId === useGetListSyncQueryKey
  )
}

export const useGetListSync = (
  params: Partial<PathParameters & QueryParameters>,
  config?: Omit<
    UseQueryOptions<
      TData,
      TError,
      TData,
      ReturnType<(typeof integrationsKeys)['listSync']>
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const queryClient = useQueryClient()

  return useQuery(
    integrationsKeys.listSync({
      localEntity: params.local_entity ?? '',
      localEntityRef: params?.local_entity_ref?.toString() ?? '',
      integrationId: params?.integration_id ?? '',
    }),
    () => listSync(params),
    {
      ...config,
      onSuccess: (data) => {
        const latestSyncResponse = last(data)

        if (
          latestSyncResponse?.status ===
            IntegrationSyncResponseStatuses.COMPLETED ||
          latestSyncResponse?.status ===
            IntegrationSyncResponseStatuses.MARKED_AS_SYNCED
        ) {
          queryClient.setQueryDefaults(
            integrationsKeys.listSync({
              localEntity: latestSyncResponse.local_entity,
              localEntityRef: latestSyncResponse.local_entity_ref ?? '',
              integrationId: latestSyncResponse.integration_id,
            }),
            {
              staleTime: Infinity,
            }
          )
        }
      },
    }
  )
}
